import React, { useState, useMemo, useCallback } from 'react';
import { Auth } from 'app/scripts/db/auth';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import styles from './ChooseMember.less';
import { CheckIcon } from '@trello/nachos/icons/check';
import { forTemplate, localizeCount } from '@trello/i18n';
import { buildFuzzyMatcher } from '@trello/strings';
import { buildComparator } from '@trello/arrays';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { Button } from '@trello/nachos/button';
import { Key, getKey } from '@trello/keybindings';
import cx from 'classnames';
import { ChecklistTestIds } from '@trello/test-ids';

const format = forTemplate('choose_member');

interface Member {
  id: string;
  fullName: string;
  username: string;
  initials: string;
}

type MemberGetter<M extends Member> = () => M[];

interface ChooseMemberProps<M extends Member> {
  idMemberAssigned?: string | null;
  getCardMembers: MemberGetter<M>;
  getBoardMembers: MemberGetter<M>;
  onSelect: (member: M | null) => void;
  testId?: ChecklistTestIds;
}

interface MemberRowProps<M extends Member> {
  member: M;
  selected: boolean;
  highlight: boolean;
  searching: boolean;
  toggleMember: (member: M) => void;
  setManualMember: (member: M | undefined) => void;
}

const MAX_MEMBERS_IN_SECTION = 12;

const MemberRow = <M extends Member>({
  highlight,
  member,
  selected,
  searching,
  toggleMember,
  setManualMember,
}: MemberRowProps<M>) => {
  const onClick = useCallback(() => toggleMember(member), [
    member,
    toggleMember,
  ]);
  const onFocus = useCallback(() => setManualMember(undefined), [
    setManualMember,
  ]);
  const onMouseOver = useCallback(() => {
    if (searching) {
      setManualMember(member);
    }
  }, [member, searching, setManualMember]);

  return (
    <li>
      <button
        className={cx(styles.memberButton, { [styles.highlight]: highlight })}
        onMouseOver={onMouseOver}
        onFocus={onFocus}
        onClick={onClick}
        data-test-id={ChecklistTestIds.ChecklistItemAddMemberButton}
      >
        <MemberAvatar className={styles.memberAvatar} idMember={member.id} />
        <div className={styles.memberName}>{member.fullName}</div>
        {selected && <CheckIcon />}
      </button>
    </li>
  );
};

function useFilterMembers<M extends Member>(
  search: string,
  getCardMembers: MemberGetter<M>,
  getBoardMembers: MemberGetter<M>,
  idMemberAssigned?: string | null,
) {
  return useMemo(() => {
    const matcher = buildFuzzyMatcher(search);
    const isMatch = ({ fullName, initials, username }: Member) =>
      [fullName, initials, username].some(matcher);

    //Follows Card Member Popover Select Sort; Member is always first, then active,
    //then alphabetical.

    const comparator = buildComparator<M>(
      (member: M) => Auth.isMe(member),
      (member: M) => member.id === idMemberAssigned,
      (a: M, b: M) => a.fullName.localeCompare(b.fullName),
    );

    const sortedCardMembers = getCardMembers().filter(isMatch).sort(comparator);
    const cardMemberIdSet = new Set(
      sortedCardMembers.map((member) => member.id),
    );

    const sortedBoardMembers = getBoardMembers()
      .filter((member) => isMatch(member) && !cardMemberIdSet.has(member.id))
      .sort(comparator);

    return { sortedCardMembers, sortedBoardMembers };
  }, [search, getCardMembers, getBoardMembers, idMemberAssigned]);
}

function adjacentMember<M extends Member>(
  members: M[],
  current: M | undefined,
  direction: 'up' | 'down',
): M | undefined {
  if (members.length === 0 || !current) {
    return undefined;
  }

  const indexCurrent = members.indexOf(current);
  const indexNext = indexCurrent + (direction === 'up' ? -1 : 1);

  // Wrap around if we reach the top/bottom
  return members[(members.length + indexNext) % members.length];
}

export function ChooseMember<M extends Member>(props: ChooseMemberProps<M>) {
  const { idMemberAssigned, onSelect } = props;

  const [searching, setSearching] = useState(true);
  const [search, setSearch] = useState('');

  const { sortedCardMembers, sortedBoardMembers } = useFilterMembers(
    search,
    props.getCardMembers,
    props.getBoardMembers,
    props.idMemberAssigned,
  );

  const allMatchingMembers = useMemo(
    () => [...sortedCardMembers, ...sortedBoardMembers],
    [sortedBoardMembers, sortedCardMembers],
  );

  const defaultMember =
    allMatchingMembers.length > 0 ? allMatchingMembers[0] : undefined;

  const [manualMember, setManualMember] = useState(defaultMember);

  const effectiveMember = searching
    ? manualMember && allMatchingMembers.includes(manualMember)
      ? manualMember
      : defaultMember
    : undefined;

  const toggleMember = useCallback(
    (member: M | undefined) =>
      member && onSelect(member.id === idMemberAssigned ? null : member),
    [idMemberAssigned, onSelect],
  );

  const onKeyboardNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (getKey(event)) {
        case Key.ArrowDown:
          setManualMember(
            adjacentMember(allMatchingMembers, effectiveMember, 'down'),
          );
          break;

        case Key.ArrowUp:
          setManualMember(
            adjacentMember(allMatchingMembers, effectiveMember, 'up'),
          );
          break;

        case Key.Enter:
          toggleMember(effectiveMember);
          break;

        default:
          return;
      }

      event.preventDefault();
    },
    [allMatchingMembers, effectiveMember, toggleMember],
  );

  const renderMembers = (members: M[]) => (
    <>
      <ul className={styles.memberList}>
        {members.slice(0, MAX_MEMBERS_IN_SECTION).map((member) => {
          const selected = member.id === idMemberAssigned;

          return (
            <MemberRow<M>
              key={member.id}
              member={member}
              selected={selected}
              highlight={member === effectiveMember}
              searching={searching}
              toggleMember={toggleMember}
              setManualMember={setManualMember}
            />
          );
        })}
      </ul>
      {members.length > MAX_MEMBERS_IN_SECTION && (
        <div className={styles.memberOverflow}>
          <div>
            {localizeCount(
              'hiding-additional-members',
              members.length - MAX_MEMBERS_IN_SECTION,
            )}
          </div>
          <div>{format('search-to-find-more')}</div>
        </div>
      )}
    </>
  );

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setManualMember(undefined);
  }, []);

  const onBlur = useCallback(() => setSearching(false), []);
  const onFocus = useCallback(() => setSearching(true), []);
  const onClick = useCallback(() => onSelect(null), [onSelect]);

  return (
    <ComponentWrapper>
      <input
        autoFocus
        className="js-autofocus"
        type="text"
        placeholder={format('search-members')}
        onChange={onChange}
        onKeyDown={onKeyboardNavigation}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {sortedCardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('card-members')}</h4>
          {renderMembers(sortedCardMembers)}
        </>
      )}

      {sortedBoardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('other-board-members')}</h4>
          {renderMembers(sortedBoardMembers)}
        </>
      )}
      {}
      <Button
        className={styles.removeButton}
        onClick={onClick}
        shouldFitContainer={true}
        isDisabled={!idMemberAssigned}
        data-test-id={ChecklistTestIds.ChecklistItemRemoveMemberButton}
      >
        {format('remove-member')}
      </Button>
    </ComponentWrapper>
  );
}
